import recipeManifest from "../../../../config/questionnaire-recipe-registry.json";
import { profilingRoleOf } from "./profiling";

/**
 * Recipe modules whose `end_survey` removes people who do not qualify.
 * Generated from Python `RecipeModule.screenOutIfEnded`.
 */
const SCREEN_OUT_IF_ENDED_MODULE_IDS = new Set<string>(
  recipeManifest.screenOutIfEndedModuleIds,
);

function isScreenOutModule(recipeModuleId: string | null | undefined): boolean {
  return (
    typeof recipeModuleId === "string" &&
    recipeModuleId.length > 0 &&
    SCREEN_OUT_IF_ENDED_MODULE_IDS.has(recipeModuleId)
  );
}

function stampedRecipeModuleId(question: {
  recipeModuleId?: string | null;
  planAttribution?: { recipeModuleId?: string | null } | null;
}): string | null {
  if (typeof question.recipeModuleId === "string" && question.recipeModuleId.length > 0) {
    return question.recipeModuleId;
  }
  const stamped = question.planAttribution?.recipeModuleId;
  if (typeof stamped === "string" && stamped.length > 0) {
    return stamped;
  }
  return null;
}

/**
 * Whether an `end_survey` on this question removes someone who does not qualify.
 *
 * Eligibility is the screening role AUTHOR stamps, or the `profile:eligibility`
 * tag that survives after the canonical role is `research`. Sample gates are
 * the recipe modules flagged `screenOutIfEnded`.
 */
function isQualificationExit(question: {
  role?: string | null;
  tags?: ReadonlyArray<string> | null;
  recipeModuleId?: string | null;
  planAttribution?: { recipeModuleId?: string | null } | null;
}): boolean {
  if (question.role === "screening") {
    return true;
  }
  if (profilingRoleOf(question.tags) === "eligibility") {
    return true;
  }
  return isScreenOutModule(stampedRecipeModuleId(question));
}

/**
 * Authoring default for an `end_survey` rule.
 *
 * Same qualification exit as a retained route (`isQualificationExit`):
 * the screening role AUTHOR stamps, the `profile:eligibility` tag
 * `profilingRoleOf` reads on a persisted research question, or a
 * `screenOutIfEnded` module. A measured early ending stays
 * `branch_complete`. This does not relabel a disposition already stored
 * on a rule.
 */
export function defaultEndSurveyDisposition(question: {
  role?: string | null;
  tags?: ReadonlyArray<string> | null;
  recipeModuleId?: string | null;
  planAttribution?: { recipeModuleId?: string | null } | null;
}): "branch_complete" | "screened_out" {
  return isQualificationExit(question) ? "screened_out" : "branch_complete";
}

/**
 * A rewrite keeps the question's existing route. When the stamped replacement
 * is a qualification exit, that retained `end_survey` is a screen-out.
 * `skip_to` and a measured ending stay as stored.
 */
export function normalizeRetainedEndSurveyDisposition<T extends {
  action?: string | null;
  disposition?: "branch_complete" | "screened_out" | null;
}>(rule: T, question: {
  role?: string | null;
  tags?: ReadonlyArray<string> | null;
  recipeModuleId?: string | null;
  planAttribution?: { recipeModuleId?: string | null } | null;
}): T {
  if (rule == null || typeof rule !== "object" || rule.action !== "end_survey") {
    return rule;
  }
  if (!isQualificationExit(question) || rule.disposition === "screened_out") {
    return rule;
  }
  return { ...rule, disposition: "screened_out" } as T;
}
